'use client';
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from '@benzinga/themetron';
import CurrencyInputWrapper from '../MortgageCalculator/CurrencyInputWrapper';
import { NoFirstRender } from '@benzinga/hooks';
import AnimatedNumber from 'animated-number-react';
import { YearlyPricePredictionData } from '@benzinga/content-manager';
import { getColorByValue } from '@benzinga/frontend-utils';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Icon, Tooltip } from '@benzinga/core-ui';

export interface PricePredictionCalculatorProps {
  btn_link?: string;
  btn_label?: string;
  predictions: { [year: string]: YearlyPricePredictionData };
  title?: string;
  price: number;
  type: 'stock' | 'crypto';
  symbol?: string;
}

const inputStyles = {
  backgroundColor: ' #fefefe',
  border: '1px solid #ccc',
  color: '#0a0a0a',
} as React.CSSProperties;

export const PricePredictionCalculator: React.FC<PricePredictionCalculatorProps> = ({
  btn_label,
  btn_link,
  predictions,
  price,
  symbol,
  title,
  type,
}) => {
  const theme = useTheme();

  const predictionData = React.useMemo(() => predictions, [predictions]);
  const [update, setUpdate] = useState<boolean>(false);
  const years = Object.keys(predictionData || {});
  const [selectedYear, setSelectedYear] = useState<number>(years.length > 0 ? Number(years[1]) : 2025);
  const [investingAmount, setInvestingAmount] = useState<number>(0);
  const [unitPrice, setUnitPrice] = useState<number>(price);
  const [totalUnit, setTotalUnit] = useState<number>(0);

  const [bullishPrediction, setBullishPrediction] = useState<number>(0);
  const [averagePrediction, setAveragePrediction] = useState<number>(0);
  const [bearishPrediction, setBearishPrediction] = useState<number>(0);
  const [, setAverageROI] = useState<number>(0);

  const [, setBullishPredictionPercent] = useState<number>(0);
  const [averagePredictionPercent, setAveragePredictionPercent] = useState<number>(0);
  const [bearishPredictionPercent, setBearishPredictionPercent] = useState<number>(0);

  const handleEsitmatedReturnChange = React.useCallback(() => {
    const totalUnit = investingAmount / unitPrice;

    setTotalUnit(totalUnit);
    const bullishProjection: number = predictionData[selectedYear]?.max
      ? getPriceFromPrediction(predictionData[selectedYear]?.max) * Number(totalUnit)
      : 0;

    const averageProjection: number = predictionData[selectedYear]?.avg
      ? getPriceFromPrediction(predictionData[selectedYear]?.avg) * Number(totalUnit)
      : 0;

    const bearishProjection: number = predictionData[selectedYear]?.min
      ? getPriceFromPrediction(predictionData[selectedYear]?.min) * Number(totalUnit)
      : 0;

    setBullishPrediction(bullishProjection);
    setAveragePrediction(averageProjection);
    setBearishPrediction(bearishProjection);

    const bullishPercent = ((bullishProjection - investingAmount) / investingAmount) * 100;
    const averagePercent = ((averageProjection - investingAmount) / investingAmount) * 100;
    const bearishPercent = ((bearishProjection - investingAmount) / investingAmount) * 100;

    setBullishPredictionPercent(bullishPercent);
    setAveragePredictionPercent(averagePercent);
    setBearishPredictionPercent(bearishPercent);

    const potentionalROI = (bullishProjection + averageProjection + bearishProjection) / 3;

    setAverageROI(potentionalROI);
  }, [selectedYear, investingAmount, unitPrice, predictionData]);

  useEffect(() => {
    if (update) {
      handleEsitmatedReturnChange();
      setUpdate(false);
    }
  }, [update, selectedYear, investingAmount, unitPrice, handleEsitmatedReturnChange]);

  const getPriceFromPrediction = (predictionString?: string): number => {
    if (!predictionString) return 0;
    return Number(predictionString.replace('$', '').replace(',', ''));
  };

  useEffect(() => {
    handleEsitmatedReturnChange();
  }, [handleEsitmatedReturnChange]);

  const onChangePredictionYear = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedYear = e.target.value;
    setUpdate(true);
    setSelectedYear(Number(selectedYear));
  }, []);

  const onChangeInvestingAmount = React.useCallback((floatvalue: string) => {
    setInvestingAmount(Number(floatvalue));
    setUpdate(true);
  }, []);

  const onChangeUnitPrice = React.useCallback((floatvalue: string) => {
    setUnitPrice(Number(floatvalue));
    setUpdate(true);
  }, []);

  const isTradeInProfit = (investment: number, totalReturn: number) => {
    if (investment && totalReturn) {
      if (typeof investment === 'number' && typeof totalReturn === 'number') {
        return totalReturn > investment ? theme.colorPalette.green500 : theme.colorPalette.red500;
      }
    }
    return theme.colors.foreground;
  };

  return (
    <PricePredictionCalculatorWrap className="bg-white box-border flex flex-col h-auto w-full justify-center p-4 pb-6 relative wrap-block flex-col bg-white border border-t-4 shadow-sm relative">
      <h3 className="text-2xl font-bold mb-0">See How Much Your {symbol} Shares Could Be Worth</h3>
      {title}
      <div className="flex absolute top-2 right-2">
        <Tooltip
          className={'price-pred-tooltip'}
          content={
            <>
              <ul>
                <li>Enter your investment amount (e.g. $1,000).</li>
                <li>Select a prediction year to see projected returns.</li>
                <li>Review the results:</li>
                <ul>
                  <li>Bullish: Optimistic forecast</li>
                  <li>Average: Mid-range forecast</li>
                  <li>Bearish: Conservative forecast</li>
                </ul>
              </ul>
              <p>Your Estimated Return updates automatically based on the selected year’s forecast.</p>
              <p>
                Note: Forecast data is sourced externally and updated periodically. Figures are estimates, not
                guarantees.
              </p>
            </>
          }
          width={500}
        >
          <Icon
            className="info-circle-icon text-sm flex items-center justify-center text-[#99AECC]"
            icon={faInfoCircle}
          />
        </Tooltip>
      </div>
      <div className="price-pred-calc" id="purchase-calculator">
        <div className="price-pred-calc-inputs">
          <div className="input-item mt-2">
            <CurrencyInputWrapper
              inputName="investing_amount"
              labelText={`How much are you investing in ${symbol}?`}
              onChange={(floatvalue: string) => onChangeInvestingAmount(floatvalue)}
              value={0}
            />
          </div>
          <div className="input-item mt-2">
            <CurrencyInputWrapper
              inputName="unit_price"
              labelText="Current price"
              onChange={(floatvalue: string) => onChangeUnitPrice(floatvalue)}
              value={price}
            />
          </div>

          <div className="input-item mt-2">
            <label className="w-full text-left text-xs font-semibold" htmlFor="loan_program">
              Select prediction year
            </label>
            <select
              className="relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
              id="loan_program"
              name="loan_program"
              onChange={onChangePredictionYear}
              style={inputStyles}
              value={selectedYear}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="price-pred-calc-results mt-2">
          <h3 className="font-semibold">Bullish Return</h3>
          <h2 className="font-bold text-4xl">
            <NoFirstRender>
              {bullishPrediction > 0 ? (
                <span
                  style={{
                    color: isTradeInProfit(investingAmount, bullishPrediction),
                  }}
                >
                  <AnimatedNumber
                    formatValue={(value: number) =>
                      value ? `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '–'
                    }
                    value={bullishPrediction || 0}
                  />
                </span>
              ) : (
                <>-</>
              )}
            </NoFirstRender>
          </h2>
          <table className="w-full table-auto mb-4 mt-4">
            <tr>
              <td>
                <h3>Estimated {type && type === 'crypto' ? 'Coins' : 'Shares'}</h3>
              </td>
              <td className="text-right">
                <h3 className="font-bold text-1xl">
                  <NoFirstRender>
                    <AnimatedNumber
                      formatValue={(value: number) => (value ? `${value.toFixed(4)}` : '–')}
                      value={totalUnit || 0}
                    />
                  </NoFirstRender>
                </h3>
              </td>
            </tr>
            {/* <tr>
              <td>
                <h3>Bullish</h3>
              </td>
              <td className="text-right">
                <h3 className="font-bold text-1xl">
                  <NoFirstRender>
                    <span
                      style={{
                        color: isTradeInProfit(investingAmount, bullishPrediction),
                      }}
                    >
                      <AnimatedNumber
                        formatValue={(value: number) =>
                          value ? `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '–'
                        }
                        value={bullishPrediction || 0}
                      />
                    </span>
                    <span
                      className="ml-1 px-0.5"
                      style={{
                        backgroundColor: `${getColorByValue(theme, bullishPredictionPercent)}20`,
                        color: getColorByValue(theme, bullishPredictionPercent),
                      }}
                    >
                      ({bullishPredictionPercent ? `${bullishPredictionPercent.toFixed(0)}%` : '-'})
                    </span>
                  </NoFirstRender>
                </h3>
              </td>
            </tr> */}
            <tr>
              <td>
                <h3>Mid Forecast</h3>
              </td>
              <td className="text-right">
                <h3 className="font-bold text-1xl">
                  <NoFirstRender>
                    <span
                      style={{
                        color: isTradeInProfit(investingAmount, averagePrediction),
                      }}
                    >
                      <AnimatedNumber
                        formatValue={(value: number) =>
                          value ? `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '–'
                        }
                        value={averagePrediction || 0}
                      />
                    </span>
                    <span
                      className="ml-1 px-0.5"
                      style={{
                        backgroundColor: `${getColorByValue(theme, averagePredictionPercent)}20`,
                        color: getColorByValue(theme, averagePredictionPercent),
                      }}
                    >
                      ({averagePredictionPercent ? `${averagePredictionPercent.toFixed(0)}%` : '-'})
                    </span>
                  </NoFirstRender>
                </h3>
              </td>
            </tr>
            <tr>
              <td>
                <h3>Bearish</h3>
              </td>
              <td className="text-right">
                <h3 className="font-bold text-1xl">
                  <NoFirstRender>
                    <span
                      style={{
                        color: isTradeInProfit(investingAmount, bearishPrediction),
                      }}
                    >
                      <AnimatedNumber
                        formatValue={(value: number) =>
                          value ? `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '–'
                        }
                        value={bearishPrediction || 0}
                      />
                    </span>
                    <span
                      className="ml-1 px-0.5"
                      style={{
                        backgroundColor: `${getColorByValue(theme, bearishPredictionPercent)}20`,
                        color: getColorByValue(theme, bearishPredictionPercent),
                      }}
                    >
                      ({bearishPredictionPercent ? `${bearishPredictionPercent.toFixed(0)}%` : '-'})
                    </span>
                  </NoFirstRender>
                </h3>
              </td>
            </tr>
          </table>
          {btn_link && (
            <a
              className="explore-btn appearance-none antialiased rounded text-center w-full h-10 mt-1 font-semibold uppercase text-sm flex items-center justify-center translate-x-0"
              href={btn_link}
              type="button"
            >
              {btn_label || 'Find Brokers'}
            </a>
          )}
        </div>
      </div>
    </PricePredictionCalculatorWrap>
  );
};

const PricePredictionCalculatorWrap = styled.div`
  margin-top: 1rem;
  border-top-color: rgb(255, 90, 31);
  .price-pred-calc {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
    .price-pred-calc-inputs,
    .price-pred-calc-results {
      grid-column: span 1 / span 1;
    }
    .error {
      color: red;
      display: block;
      font-size: 0.8rem;
    }
    @media (max-width: 800px) {
      .price-pred-calc-inputs,
      .price-pred-calc-results {
        grid-column: span 2 / span 2;
      }
    }
    .explore-btn {
      display: flex;
      background-color: #ff7b00;
      color: #fefefe;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.21);
      text-shadow: 1px 0 0 rgba(0, 0, 0, 0.1);
      transition: all 0.15s ease-in-out;
      font-family: sans-serif;
      letter-spacing: 0.09em;
      min-width: 120px;
      &:hover {
        transform: translateY(-1px);
        box-shadow:
          0 2px 1px rgba(50, 50, 93, 0.2),
          0 3px 6px rgba(0, 0, 0, 0.1);
      }
    }
  }
`;
